import React, { useEffect, useRef, useState } from "react";
import { Button, NumberInput, required, SelectInput, useNotify, useRecordContext, useRefresh } from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { Grid } from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { administrationRequests } from "api/administrationApi";
import { CourseChoices } from "utils/choices";
import FormModal from "server/components/Modals/FormModal";
import dataProvider from "server/dataProvider";
import { useNotifyError } from "utils/useNotifyError";

// Спецификациите са по курс. Началните стойности идват от defaultValues (виж loadSpecs),
// а тук реагираме само на РЪЧНА смяна на курса — зарежда неговите запазени min/max,
// а курс без спецификация ги изчиства.
const GroupSpecificationFields = ({ specs }) => {
  const { setValue } = useFormContext();
  const course = useWatch({ name: "course" });
  const isFirstRun = useRef(true);

  useEffect(() => {
    // Пропускаме първото извикване — началните min/max вече идват от defaultValues.
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    const match = specs.find(s => s.course === course);
    setValue("minSize", match ? match.minSize : null);
    setValue("maxSize", match ? match.maxSize : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course]);

  return (
    <Grid
      container
      sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
    >
      <SelectInput
        source="course"
        label="Курс"
        choices={CourseChoices}
        optionValue="id"
        optionText="name"
        validate={[required()]}
      />
      <NumberInput
        source="minSize"
        validate={required()}
        label="Mинимален размер на група"
      />
      <NumberInput
        source="maxSize"
        validate={required()}
        label="Максимален размер на група"
      />
    </Grid>
  );
};

const GroupSpecificationForm = ({ label = "Спецификации- групи" }) => {
  const record = useRecordContext();
  const notify = useNotify();
  const notifyError = useNotifyError();
  const refresh = useRefresh();

  const [specs, setSpecs] = useState([]);

  if (!record?.id) return null;

  // Изчислява се от специфичните за курса данни. onOpen се await-ва преди отварянето,
  // затова specs вече е зареден, когато Dialog монтира <Form> с тези defaultValues.
  const defaultValues =
    specs.length > 0
      ? { course: specs[0].course, minSize: specs[0].minSize, maxSize: specs[0].maxSize }
      : { course: null, minSize: null, maxSize: null };

  const loadSpecs = async () => {
    try {
      const { data } = await dataProvider.customRequest(
        administrationRequests.getSpecialityGroupSpecification(record.id),
        null,
        "GET",
      );
      setSpecs(Array.isArray(data) ? data : []);
    } catch (error) {
      // 404 = специалността още няма спецификации → отваряме с празна форма
      if (error?.status === 404) {
        setSpecs([]);
        return;
      }
      notifyError(error);
      return false;
    }
  };

  const handleConfirm = async ({ course, minSize, maxSize }) => {
    await dataProvider.customRequest(
      administrationRequests.updateSpecialityGroupSpecification(record.id),
      { course, minSize, maxSize },
      "PUT",
    );
    notify("Успешно заявена промяна", { type: "success" });
    refresh();
  };

  return (
    <FormModal
      titleContent={`Групова спецификация за ${record.nameBg}`}
      saveTitle="Запази"
      defaultValues={defaultValues}
      onOpen={loadSpecs}
      onConfirm={handleConfirm}
      triggerButton={
        <Button label={label}>
          <ListAltIcon />
        </Button>
      }
    >
      <GroupSpecificationFields specs={specs} />
    </FormModal>
  );
};

export default GroupSpecificationForm;
